import React, { useCallback, useEffect, useState } from "react";
import { MoreHorizontal, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import PageHeader from "src/components/common/PageHeader";
import Button from "src/components/ui/Button";
import CardHeader from "src/components/common/card/CardHeader";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as adjustmentOrderActions from "src/redux/slices/adjustmentOrderSlice";
import { formatDateTime } from "src/utils/date-time";
import { formatCreatedBy } from "src/utils/common";

const AdjustmentListPage: React.FC = () => {
  const [limit] = useState(10);

  const { list } = useAppSelector((state) => state.adjustmentOrder);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions =
    user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateAdjustment =
    isTenantAdmin || (permissions?.adjustments || []).includes("create");
  const canEditAdjustment =
    isTenantAdmin || (permissions?.adjustments || []).includes("edit");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getAdjustments = useCallback(
    (page: number) => {
      dispatch(
        adjustmentOrderActions.getAdjustmentOrderListRequest({
          page,
          limit,
        })
      );
    },
    [dispatch, limit]
  );

  useEffect(() => {
    getAdjustments(1);
  }, [getAdjustments]);

  const handlePageChange = (page: number) => getAdjustments(page);

  return (
    <DashboardLayout
      meta={{
        title: "Adjustments | Invyntra",
        description:
          "Track and record all manual stock adjustments in the inventory.",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Stock Adjustments"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Adjustments", path: "/app/adjustment/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Adjustment Orders"
            description="Manual stock corrections and physical count updates."
            actions={
              canCreateAdjustment ? (
                <Button
                  variant="primary"
                  onClick={() => navigate("/app/adjustment/create")}
                >
                  <Plus size={16} className="mr-1" /> Add Adjustment
                </Button>
              ) : null
            }
          />

          <TableV2
            columns={[
              {
                key: "adjustmentOrderId",
                header: "Adjustment ID",
                accessor: ({ row }) => <span>#{row.adjustmentOrderId}</span>,
              },
              {
                key: "products",
                header: "Products",
                accessor: ({ row }) => (
                  <ul className="list-decimal">
                    {row.products?.map((p: any) => (
                      <li key={`${row._id}-${p.productId?._id || p.productId}`}>
                        {p.productId?.name} {" "}
                        <span
                          className={
                            p.direction === "in"
                              ? "text-green-500"
                              : "text-red-500"
                          }
                        >
                          ({p.direction})
                        </span>
                      </li>
                    ))}
                  </ul>
                ),
              },
              { key: "remarks", header: "Remarks" },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Date",
                align: "center",
                accessor: ({ row }) => (
                  <div>
                    {formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A")}
                  </div>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) =>
                  canEditAdjustment ? (
                    <Popover
                      trigger={
                        <button className="p-1 hover:bg-gray-100 rounded-md">
                          <MoreHorizontal size={18} />
                        </button>
                      }
                    >
                      <div className="py-1">
                        <button
                          className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                          onClick={() =>
                            navigate(`/app/adjustment/${row._id}/edit`)
                          }
                        >
                          Edit
                        </button>
                      </div>
                    </Popover>
                  ) : null,
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdjustmentListPage;
